import { Github, Twitter, Linkedin, Heart, Mail, ArrowRight, Globe, Shield, Zap } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

export default function Footer() {
  const navigate = useNavigate();

  const footerLinks = {
    discover: [
      { name: 'Image Compressor', path: '/tools/image-compression-tool' },
      { name: 'PDF Tools', path: '/category/pdf-tools' },
      { name: 'Developer Tools', path: '/category/dev-tools' },
      { name: 'Color Picker', path: '/tools/image-color-picker' },
      { name: 'QR Code Generator', path: '/tools/qr-code-generator-tool' },
    ],
    categories: [
      { name: 'Text & Content', path: '/category/text-tools' },
      { name: 'Image & Media', path: '/category/image-tools' },
      { name: 'Development', path: '/category/dev-tools' },
      { name: 'Calculators', path: '/category/calculator-tools' },
      { name: 'Converters', path: '/category/converters-tools' },
    ],
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Contact', path: '/contact' },
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' },
      { name: 'Disclaimer', path: '/disclaimer' },
    ]
  };

  return (
    <footer className="bg-white dark:bg-slate-950/50 border-t border-slate-100 dark:border-white/5 pt-16 pb-12 mt-auto transition-colors duration-500">
      <div className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
          {/* Brand & Newsletter - Spans 4 columns */}
          <div className="lg:col-span-4 space-y-8">
            <div className="flex flex-col items-start gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 group"
              >
                <div className="relative">
                  <div className="absolute -inset-2 bg-primary/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                  <img
                    src="/logo.png"
                    alt="VibeTools Logo"
                    className="w-10 h-10 object-contain relative z-10"
                  />
                </div>
                <span className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                  Vibe<span className="text-primary">Tools</span>
                </span>

              </button>
              <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-sm">
                Premium online utilities for modern creators.
                <br />
                Secure, client-side, and always free.
              </p>

            </div>
          </div>

          {/* Links Grid - Spans 8 columns */}
          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-8 lg:gap-12">
            {/* Discover Column */}
            <div>
              <h4 className="font-bold text-slate-900 dark:text-slate-200 mb-6 uppercase tracking-wider text-xs">Discover</h4>

              <ul className="space-y-3">
                {footerLinks.discover.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-slate-500 dark:text-slate-400 hover:text-primary font-medium transition-colors hover:translate-x-1 inline-flex items-center gap-2 text-sm"
                    >

                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Categories Column */}
            <div>
              <h4 className="font-bold text-slate-900 dark:text-slate-200 mb-6 uppercase tracking-wider text-xs">Categories</h4>

              <ul className="space-y-3">
                {footerLinks.categories.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-slate-500 hover:text-primary font-medium transition-colors hover:translate-x-1 inline-block text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="font-bold text-slate-900 dark:text-slate-200 mb-6 uppercase tracking-wider text-xs">Company</h4>

              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-slate-500 hover:text-primary font-medium transition-colors hover:translate-x-1 inline-block text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
                <li className="pt-4">
                  <a
                    href="mailto:support@vibetools.online"
                    className="flex items-center gap-2 text-slate-900 dark:text-slate-100 font-bold p-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-white/10 rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 hover:border-primary/20 dark:hover:border-primary/30 hover:text-primary transition-all group shadow-sm"
                  >

                    <Mail size={16} />
                    <span className="text-xs">Contact Support</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-200 dark:border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col md:flex-row items-center gap-4 text-xs font-semibold text-slate-500 dark:text-slate-400">

            <p>© {new Date().getFullYear()} VibeTools. All rights reserved.</p>
            <span className="hidden md:inline text-slate-300">•</span>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5"><Shield size={12} className="text-green-500" /> Privacy First</span>
              <span className="flex items-center gap-1.5"><Zap size={12} className="text-amber-500" /> Client-Side</span>
              <span className="flex items-center gap-1.5"><Globe size={12} className="text-primary" /> Global</span>
            </div>

          </div>

          <div className="flex items-center gap-6">
            <a href="#" className="text-slate-400 hover:text-primary transition-colors" aria-label="Twitter">
              <Twitter size={18} />
            </a>
            <a href="#" className="text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors" aria-label="GitHub">
              <Github size={18} />
            </a>
            <a href="#" className="text-slate-400 hover:text-primary transition-colors" aria-label="LinkedIn">
              <Linkedin size={18} />
            </a>
            <div className="w-px h-5 bg-slate-200 dark:bg-white/10 mx-2"></div>
            <div className="flex items-center gap-2 text-xs font-bold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 border border-gray-100 dark:border-white/5 px-3 py-1.5 rounded-full shadow-sm">
              Made with <Heart size={12} className="text-red-500 fill-red-500 animate-pulse" />
            </div>

          </div>
        </div>
      </div>
    </footer>
  );
}
